import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, PlayCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/sea/index.css';
// HLS desteği için
import '@videojs/http-streaming';
import 'videojs-hls-quality-selector';
import './App.css';
import { auth } from './firebase';
import { updateLastWatchedVideo, addCompletedLesson } from './utils/userService';
import { useLandingPageCleanup } from './contexts/LandingPageContext';

const VIDEO_ASPECT_RATIO = 16 / 9;

const createDefaultLayoutMetrics = () => ({
  sidebarWidth: null,
  videoWidth: null,
  videoHeight: null,
  topBarHeight: null,
  variant: null,
  verticalDelta: 0
});

const LecturePage = (props) => {
  const { isCleaningUp, cleanupComplete } = useLandingPageCleanup();
  const courseData = props.courseData;
  const userData = props.userData;

  // lastWatchedVideo'dan sectionId ve lessonId al
  let sectionId = 1;
  let lessonId = null;
  if (userData?.lastWatchedVideo?.lessonId) {
    lessonId = userData.lastWatchedVideo.lessonId;
    sectionId = userData.lastWatchedVideo.sectionId;
  }

  // sectionId ve lessonId kullanarak lesson'u bul
  const getInitialLesson = () => {
    if (lessonId) {
      for (const section of courseData.sections) {
        if (section.id === sectionId) {
          const lesson = section.lessons.find(l => l.id === lessonId);
          if (lesson) {
            return lesson;
          }
        }
      }
    }
    // Bulunamazsa veya yoksa ilk lesson'u döndür
    return courseData.sections[0].lessons[0];
  };

  // İlk lesson'un section'ını bul
  const getInitialSectionId = () => {
    const initialLesson = getInitialLesson();
    for (const section of courseData.sections) {
      if (section.lessons.some(lesson => lesson.id === initialLesson.id)) {
        return section.id;
      }
    }
    return 1;
  };

  const [currentLesson, setCurrentLesson] = useState(getInitialLesson());
  const [expandedSections, setExpandedSections] = useState({ [getInitialSectionId()]: true });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layoutMetrics, setLayoutMetrics] = useState(createDefaultLayoutMetrics);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [completedLessons, setCompletedLessons] = useState(userData?.completedLessons || []);
  const [playerReady, setPlayerReady] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState('auto');//manuel quality selector için seçili kalite
  const [selectedPlaybackRate, setSelectedPlaybackRate] = useState(1);//seçili playback rate

  // Video.js refs
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const isEndedRef = useRef(false);
  const hasUserInteractedRef = useRef(false);
  const wasFullscreenRef = useRef(false);
  const completionReportedRef = useRef(false);
  const currentLessonRef = useRef(currentLesson);
  const mutationObserverRef = useRef(null);
  const completedLessonsRef = useRef(completedLessons);
  const lastSavedPositionRef = useRef(0);
  const selectedPlaybackRateRef = useRef(1);

  // Mobil cihaz kontrolü
  const isMobileDevice = useCallback(() => {
    // 1. Touch event desteği kontrolü
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // 2. User Agent kontrolü
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    
    // 3. Ekran genişliği kontrolü
    const isSmallScreen = window.innerWidth < 1024;
    
    // En az iki kriter sağlanıyorsa mobil kabul et
    const mobileIndicators = [hasTouchScreen, isMobileUA, isSmallScreen].filter(Boolean).length;
    return mobileIndicators >= 2;
  }, []);

  const evaluateDesktopLayout = useCallback(() => {
    if (typeof window === 'undefined') return;

    const pageWidth = window.innerWidth;
    const pageHeight = window.innerHeight;

    const mobile = isMobileDevice();
    setIsMobileLayout(mobile);

    if (mobile) {
      setLayoutMetrics(createDefaultLayoutMetrics());
      //console.log('[LayoutEval] Mobil/Tablet düzeni, hesaplama yapılmadı.');
      return;
    }

    const minSidebarWidth = pageWidth * 0.2;
    const maxSidebarWidth = pageWidth * 0.3;

    let topBarHeight = 56;
    if (pageWidth >= 1536) {
      topBarHeight = 88;
    } else if (pageWidth >= 640) {
      topBarHeight = 72;
    }

    const availableHeightForVideo = Math.max(pageHeight - topBarHeight, 0);
    const idealVideoWidth = (availableHeightForVideo * VIDEO_ASPECT_RATIO);
    const candidateSidebarWidth = pageWidth - idealVideoWidth;

    if (candidateSidebarWidth >= minSidebarWidth && candidateSidebarWidth <= maxSidebarWidth) {
      setLayoutMetrics({
        sidebarWidth: candidateSidebarWidth,
        videoWidth: idealVideoWidth,
        videoHeight: availableHeightForVideo,
        topBarHeight,
        variant: 'balanced',
        verticalDelta: 0
      });

      /*console.log('[LayoutEval] Her şey dengeli görünüyor.', {
        pageWidth,
        pageHeight,
        topBarHeight,
        sidebarWidth: candidateSidebarWidth,
        videoWidth: idealVideoWidth,
        videoHeight: availableHeightForVideo,
        leftoverSpace: 0
      });*/
      return;
    }

    if (candidateSidebarWidth < minSidebarWidth) {
      const videoWidth = pageWidth - minSidebarWidth;
      const videoHeight = videoWidth / VIDEO_ASPECT_RATIO;
      const leftoverSpace = pageHeight - (topBarHeight + videoHeight);

      setLayoutMetrics({
        sidebarWidth: minSidebarWidth,
        videoWidth,
        videoHeight,
        topBarHeight,
        variant: 'sidebarMin',
        verticalDelta: leftoverSpace
      });

      /*console.log('[LayoutEval] Yükseklik kısa kaldı, sidebar minimumda olmalı.', {
        pageWidth,
        pageHeight,
        topBarHeight,
        sidebarWidth: minSidebarWidth,
        videoWidth,
        videoHeight,
        leftoverSpace
      });*/
      return;
    }

    const videoWidth = pageWidth - maxSidebarWidth;
    const videoHeight = videoWidth / VIDEO_ASPECT_RATIO;
    const overflow = (topBarHeight + videoHeight) - pageHeight;

    setLayoutMetrics({
      sidebarWidth: maxSidebarWidth,
      videoWidth,
      videoHeight,
      topBarHeight,
      variant: 'sidebarMax',
      verticalDelta: -overflow
    });

    /*console.log('[LayoutEval] Yükseklik yetersiz, sidebar %30 yapılmalı.', {
      pageWidth,
      pageHeight,
      topBarHeight,
      sidebarWidth: maxSidebarWidth,
      videoWidth,
      videoHeight,
      overflow
    });*/
  }, [isMobileDevice]);

  useEffect(() => {
    setCompletedLessons(userData?.completedLessons || []);
  }, [userData]);

  // Safari tespiti (Mac ve iOS)
  const isSafari = () => {
    const ua = navigator.userAgent;
    const uaLower = ua.toLowerCase();
    
    // Chrome kontrolü (iPhone'da Chrome'u Safari olarak algılamamak için)
    // CriOS = Chrome iOS, FxiOS = Firefox iOS
    if (ua.includes('CriOS') || ua.includes('FxiOS') || 
        (uaLower.includes('chrome') && !uaLower.includes('edg')) ||
        (uaLower.includes('firefox') && uaLower.includes('mobile'))) {
      return false; // Chrome veya Firefox (iPhone dahil)
    }
    
    // Mac Safari veya iOS Safari (iPhone, iPad, iPod) - ama Chrome/Firefox değil
    return /^((?!chrome|android).)*safari/i.test(ua) || 
           (/iPad|iPhone|iPod/.test(ua) && !uaLower.includes('chrome') && !uaLower.includes('crios') && !uaLower.includes('fxios'));
  };

  // Video URL tipini belirle (MP4, HLS, vb.)
  const getVideoType = (url) => {
    if (url.includes('.m3u8')) {
      // Safari için native HLS desteği
      return isSafari() ? 'application/vnd.apple.mpegurl' : 'application/x-mpegURL';
    } else if (url.includes('.mp4')) {
      return 'video/mp4';
    } else if (url.includes('.webm')) {
      return 'video/webm';
    } else if (url.includes('.ogg')) {
      return 'video/ogg';
    }
    // Varsayılan olarak MP4
    return 'video/mp4';
  };

  // Kalite değiştirme fonksiyonu
  const changeQuality = useCallback(async (qualityValue) => {
    if (!playerRef.current) return;

    // Mevcut video tipini kontrol et
    const currentSrc = playerRef.current.currentSrc();
    const currentVideoType = getVideoType(currentSrc || currentLesson.videoUrl);
    const isHLS = currentVideoType === 'application/x-mpegURL' || currentVideoType === 'application/vnd.apple.mpegurl';
    
    // HLS stream ise ve qualityLevels varsa, HLS quality levels API'sini kullan
    if (isHLS && playerRef.current.qualityLevels) {
      const qualityLevels = playerRef.current.qualityLevels();
      if (qualityLevels && qualityLevels.length > 0) {
        const levelsArray = Array.from(qualityLevels);
        
        if (qualityValue === 'auto') {
          // Auto: Tüm level'ları enable et
          levelsArray.forEach(level => {
            level.enabled = true;
          });
          console.log('HLS Auto kalite seçildi');
        } else {
          // Belirli bir kalite seçildi: height'a göre bul
          const heightMap = {
            '2160p': 2160,
            '1440p': 1440,
            '1080p': 1080,
            '720p': 720,
            '360p': 360
          };
          
          const targetHeight = heightMap[qualityValue];
          if (targetHeight) {
            const targetLevel = levelsArray.find(level => level.height === targetHeight);
            if (targetLevel) {
              // Sadece seçilen kaliteyi enable et, diğerlerini disable et
              levelsArray.forEach(level => {
                level.enabled = (level === targetLevel);
              });
              console.log(`HLS ${qualityValue} seçildi`);
            } else {
              console.warn(`HLS'de ${qualityValue} kalitesi bulunamadı`);
            }
          }
        }
        return; // HLS quality levels kullanıldı, MP4 linklere geçme
      }
    }
    
    // MP4 stream ise veya qualityLevels yoksa, mevcut MP4 link mantığını kullan
    let targetUrl;
    
    if (qualityValue === 'auto') {
      // Auto seçildiğinde master playlist'e geri dön
      targetUrl = currentLesson.videoUrl;
    } else {
      // Seçilen kaliteye göre URL'i al
      const urlKey = `url${qualityValue}`;
      targetUrl = currentLesson[urlKey];
      
      if (!targetUrl) {
        console.warn(`Bu kalite için URL bulunamadı: ${qualityValue}`);
        return;
      }
    }
    
    try {
      // Mevcut pozisyonu ve oynatma durumunu kaydet
      const currentTime = playerRef.current.currentTime();
      const wasPlaying = !playerRef.current.paused();
      
      const videoType = getVideoType(targetUrl);
      playerRef.current.src({
        src: targetUrl,
        type: videoType
      });
      playerRef.current.load();
      
      // Video yüklendikten sonra pozisyonu geri yükle
      playerRef.current.one('loadedmetadata', () => {
        playerRef.current.currentTime(currentTime);
        if (wasPlaying) {
          playerRef.current.play().catch(err => {
            console.error('Play hatası:', err);
          });
        }
      });
    } catch (error) {
      console.error('Kalite değiştirme hatası:', error);
    }
  }, [currentLesson]);

  // Video player'ı başlatan fonksiyon
  const initVideoPlayer = () => {
    if (!videoRef.current || playerRef.current) return;

    // Video element'inin DOM'da olduğundan emin ol
    if (!document.body.contains(videoRef.current)) {
      /*console.log('Video element not in DOM yet, retrying...');*/
      return false;
    }

    try {
      const videoType = getVideoType(currentLesson.videoUrl);
      
      // Video.js player'ı oluştur
      const isSafariBrowser = isSafari();
      const isHLS = videoType === 'application/x-mpegURL' || videoType === 'application/vnd.apple.mpegurl';
      
      playerRef.current = videojs(videoRef.current, {
        controls: true,
        responsive: true,
        fluid: true,
        poster: '/thumbnail_ornek.jpeg',
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
        html5: {
          vhs: {
            overrideNative: !isSafariBrowser, // Safari'de native HLS kullan
            withCredentials: false
          },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false,
          // Safari için native HLS desteği
          hls: {
            withCredentials: false
          }
        },
        sources: [{
          src: currentLesson.videoUrl,
          type: videoType
        }]
      });

      // Play event listener
      playerRef.current.on('play', () => {
        if (!hasUserInteractedRef.current) {
        hasUserInteractedRef.current = true; 
        const sonKaldigiSaniye = userData?.lastWatchedVideo?.position || 0;
        playerRef.current.currentTime(sonKaldigiSaniye);
      }

      if (isEndedRef.current) { 
        isEndedRef.current = false; 
        restartVideo();
      }
    });

      // Ended event listener
      playerRef.current.on('ended', () => {
      if (!isEndedRef.current) {
        isEndedRef.current = true;
          // Video bittiğinde direkt sonraki derse geç
          goToNextLesson();
        }
    });

      // Fullscreen change listener
      playerRef.current.on('fullscreenchange', () => {
        const isFullscreen = playerRef.current.isFullscreen();
        setIsFullscreen(isFullscreen);
      
        if (isFullscreen) {
        wasFullscreenRef.current = true;
      } else {
        if (!isEndedRef.current) {
          wasFullscreenRef.current = false;
        }
        }
      });

      // Playback rate change listener - kullanıcı hız değiştirdiğinde state'i güncelle
      playerRef.current.on('ratechange', () => {
        const currentRate = playerRef.current.playbackRate();
        if (currentRate && currentRate !== selectedPlaybackRateRef.current) {
          setSelectedPlaybackRate(currentRate);
          selectedPlaybackRateRef.current = currentRate;
        }
      });

      // Manuel quality selector'ı her zaman ekle (hem HLS hem MP4 için)
      playerRef.current.ready(() => {
        // HLS stream ise otomatik quality selector'ı gizle
        if (videoType === 'application/x-mpegURL' || videoType === 'application/vnd.apple.mpegurl') {
          // Otomatik quality selector'ı gizle (eğer varsa)
          if (playerRef.current && playerRef.current.el()) {
            const autoQualitySelector = playerRef.current.el().querySelector('.vjs-quality-selector');
            if (autoQualitySelector) {
              autoQualitySelector.style.display = 'none';
            }
          }
          
          // MutationObserver ile otomatik quality selector eklendiğinde gizle
          const controlBar = playerRef.current?.el()?.querySelector('.vjs-control-bar');
          if (controlBar && playerRef.current) {
            // Önceki observer'ı temizle
            if (mutationObserverRef.current) {
              mutationObserverRef.current.disconnect();
            }
            
            const observer = new MutationObserver(() => {
              // Null kontrolü ekle
              if (!playerRef.current || !playerRef.current.el()) {
                observer.disconnect();
                return;
              }
              const autoSelector = playerRef.current.el().querySelector('.vjs-quality-selector');
              if (autoSelector) {
                autoSelector.style.display = 'none';
              }
            });
            
            mutationObserverRef.current = observer;
            observer.observe(controlBar, {
              childList: true,
              subtree: true
            });
            
            // 3 saniye sonra observer'ı durdur
            setTimeout(() => {
              if (observer && mutationObserverRef.current === observer) {
                observer.disconnect();
                mutationObserverRef.current = null;
              }
            }, 3000);
          }
        }
        
        // Manuel quality selector ekleme fonksiyonu
          const addManualQualitySelector = () => {
            if (!playerRef.current || !playerRef.current.el()) return;
            const controlBar = playerRef.current.el().querySelector('.vjs-control-bar');
            if (controlBar && !controlBar.querySelector('.vjs-safari-quality-selector')) {
              const button = document.createElement('button');
              button.className = 'vjs-control vjs-button vjs-safari-quality-selector';
              button.setAttribute('title', 'Kalite Seç');
              button.style.cssText = 'font-family: Arial, Helvetica, sans-serif; font-size: 13px; cursor: pointer; color: white; padding: 0 0.5em;';
              
              // Button text'ini direkt ekle
              const buttonText = document.createElement('span');
              buttonText.textContent = 'Auto';
              buttonText.style.cssText = 'color: white; display: inline-block;';
              button.appendChild(buttonText);
              
              // Screen reader için de text ekle
              const screenReaderText = document.createElement('span');
              screenReaderText.className = 'vjs-control-text';
              screenReaderText.textContent = 'Kalite Seç';
              screenReaderText.style.cssText = 'display: none;';
              button.appendChild(screenReaderText);
              
              const menu = document.createElement('div');
              menu.className = 'vjs-menu vjs-safari-quality-menu';
              menu.style.cssText = 'display: none; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.9); width: 100px; z-index: 1000; border-radius: 4px; overflow: hidden;';
              
              // Kalite seçenekleri (yüksekten düşüğe)
              const qualities = [
                { label: '2160p', value: '2160p' },
                { label: '1440p', value: '1440p' },
                { label: '1080p', value: '1080p' },
                { label: '720p', value: '720p' },
                { label: '360p', value: '360p' },
                { label: 'Auto', value: 'auto' }
              ];
              
              // Seçili item'ı güncelleme fonksiyonu
              const updateSelectedItem = (currentQuality) => {
                const items = menu.querySelectorAll('.vjs-safari-quality-item');
                items.forEach(item => {
                  const itemValue = qualities.find(q => q.label === item.textContent)?.value;
                  if (itemValue === currentQuality) {
                    item.style.backgroundColor = 'rgba(147, 51, 234, 0.3)';
                    item.style.color = '#fff';
                    item.classList.add('vjs-selected');
                  } else {
                    item.style.backgroundColor = 'transparent';
                    item.style.color = 'white';
                    item.classList.remove('vjs-selected');
                  }
                });
                // Button text'i de güncelle
                const selectedQualityObj = qualities.find(q => q.value === currentQuality);
                if (selectedQualityObj) {
                  buttonText.textContent = selectedQualityObj.label;
                }
              };
              
              // İlk render'da güncelle
              updateSelectedItem(selectedQuality);
              
              qualities.forEach(quality => {
                const item = document.createElement('div');
                item.className = 'vjs-menu-item vjs-safari-quality-item';
                item.style.cssText = 'padding: 10px 15px; cursor: pointer; color: white; font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.1); transition: background-color 0.2s;';
                item.textContent = quality.label;
                
                // Başlangıçta seçili olanı işaretle
                if (quality.value === selectedQuality) {
                  item.style.backgroundColor = 'rgba(147, 51, 234, 0.3)';
                  item.classList.add('vjs-selected');
                }
                
                item.addEventListener('mouseenter', () => {
                  if (!item.classList.contains('vjs-selected')) {
                    item.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  }
                });
                
                item.addEventListener('mouseleave', () => {
                  if (!item.classList.contains('vjs-selected')) {
                    item.style.backgroundColor = 'transparent';
                  } else {
                    item.style.backgroundColor = 'rgba(147, 51, 234, 0.3)';
                  }
                });
                
                item.addEventListener('click', (e) => {
                  e.stopPropagation();
                  // State'i güncelle - useEffect kaliteyi değiştirecek
                  setSelectedQuality(quality.value);
                  menu.style.display = 'none';
                });
                
                menu.appendChild(item);
              });
              
              // State değiştiğinde UI'ı güncellemek için button'a referans sakla
              button.dataset.updateFn = 'updateSelectedItem';
              button.updateSelectedItem = updateSelectedItem;
              
              button.appendChild(menu);
              button.style.position = 'relative';
              
              button.addEventListener('click', (e) => {
                e.stopPropagation();
                menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
              });
              
              // Dışarı tıklandığında menüyü kapat
              const closeMenuOnOutsideClick = (e) => {
                if (!button.contains(e.target)) {
                  menu.style.display = 'none';
                }
              };
              
              // Control bar'a ekle (fullscreen button'dan önce)
              const fullscreenButton = controlBar.querySelector('.vjs-fullscreen-control');
              if (fullscreenButton) {
                controlBar.insertBefore(button, fullscreenButton);
              } else {
                controlBar.appendChild(button);
              }
              
              // Document click listener ekle
              setTimeout(() => {
                document.addEventListener('click', closeMenuOnOutsideClick);
              }, 100);
            }
          };
          
          // Manuel quality selector'ı ekle
          addManualQualitySelector();
          
          // Kalite menüsü için stil ekle
          setTimeout(() => {
            const styleId = 'vjs-hls-quality-selector-styles';
            if (!document.getElementById(styleId)) {
              const style = document.createElement('style');
              style.id = styleId;
              style.textContent = `
                .vjs-control-bar {
                  height: 40px !important;
                  line-height: 40px !important;
                }
                .vjs-control {
                  line-height: 40px !important;
                }
                .vjs-fullscreen-control .vjs-icon-placeholder::before,
                .vjs-play-control .vjs-icon-placeholder::before,
                .vjs-volume-panel .vjs-icon-placeholder::before {
                  position: relative !important;
                }
                .vjs-volume-panel {
                  display: flex !important;
                  align-items: center !important;
                }
                .vjs-safari-quality-menu .vjs-safari-quality-item {
                  line-height: 1em !important;
                }
                .vjs-play-control,
                .vjs-current-time-display,
                .vjs-time-control,
                .vjs-playback-rate,
                .vjs-hls-quality-selector,
                .vjs-control,
                .vjs-safari-quality-selector {
                  border-right: 1px solid rgba(255, 255, 255, 0.5) !important;
                }
                .vjs-fullscreen-control,
                .vjs-volume-panel,
                .vjs-volume-panel .vjs-volume-control,
                .vjs-volume-panel .vjs-mute-control {
                  border-right: none !important;
                }
                .vjs-hls-quality-selector {
                  font-size: 13px !important;
                }
                .vjs-hls-quality-selector .vjs-menu-content {
                  background-color: rgba(0, 0, 0, 0.9) !important;
                }
                .vjs-hls-quality-selector .vjs-menu-item {
                  color: #fff !important;
                  text-shadow: none !important;
                  font-size: 13px !important;
                }
                .vjs-hls-quality-selector .vjs-menu-item:hover {
                  background-color: rgba(255, 255, 255, 0.1) !important;
                  color: #fff !important;
                }
                .vjs-hls-quality-selector .vjs-menu-item.vjs-selected {
                  background-color: rgba(147, 51, 234, 0.3) !important;
                  color: #fff !important;
                }
                .vjs-hls-quality-selector .vjs-menu-title {
                  display: none !important;
                }
                .vjs-remaining-time-display,
                .vjs-current-time-display,
                .vjs-time-control {
                  font-size: 13px !important;
                }
                .vjs-playback-rate,
                .vjs-playback-rate .vjs-playback-rate-value {
                  font-size: 13px !important;
                  line-height: 40px !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  text-align: center !important;
                  vertical-align: middle !important;
                  height: 40px !important;
                }
                  
              `;
              document.head.appendChild(style);
            }
          }, 500);
        });

      //console.log('✅ Video.js player initialized successfully');
      return true;
    } catch (error) {
      //console.error('Error initializing Video.js:', error);
      return false;
    }
  };

  // Video.js player'ı başlat
  useEffect(() => {
    if (!videoRef.current) return;

    // DOM'un hazır olduğundan emin olmak için birkaç kez dene
    let attempts = 0;
    const maxAttempts = 10;
    
    const tryInit = () => {
      attempts++;
      
      if (initVideoPlayer()) {
        setPlayerReady(true);
        // Başarılı
        return;
      }
      
      if (attempts < maxAttempts) {
        // Tekrar dene
        setTimeout(tryInit, 100);
      } else {
        console.error('Failed to initialize Video.js after', maxAttempts, 'attempts');
      }
    };

    // İlk deneme
    const timer = setTimeout(tryInit, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      // MutationObserver'ı temizle
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
      setPlayerReady(false);
    };
  }, []); // Sadece mount'ta çalış

  // Browser fullscreen değişikliklerini dinle (Container için)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement === containerRef.current;
      setIsFullscreen(isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    evaluateDesktopLayout();
    window.addEventListener('resize', evaluateDesktopLayout);
    return () => {
      window.removeEventListener('resize', evaluateDesktopLayout);
    };
  }, [evaluateDesktopLayout]);

  const computedSidebarStyle = !isMobileLayout && layoutMetrics.sidebarWidth
    ? { width: `${layoutMetrics.sidebarWidth}px` }
    : undefined;

  const getVideoContainerStyle = () => {
    if (isFullscreen) {
      return {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        background: 'black'
      };
    }

    if (isMobileLayout || !layoutMetrics.videoWidth || !layoutMetrics.videoHeight) {
      return {
        padding: '56.25% 0 0 0',
        position: 'relative',
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        background: 'black'
      };
    }

    return {
      position: 'relative',
      width: `${layoutMetrics.videoWidth}px`,
      height: `${layoutMetrics.videoHeight}px`,
      maxWidth: '100%',
      background: 'black'
    };
  };

  // Video URL değiştiğinde güncelle
  useEffect(() => {
    if (playerRef.current && currentLesson.videoUrl) {
      isEndedRef.current = false;

      //setSelectedQuality('auto'); //her url değiştiğinde auto resetlemek istersen

      // Önceki ended event listener'ları temizle
      playerRef.current.off('ended');

      const videoType = getVideoType(currentLesson.videoUrl);
      const isSafariBrowser = isSafari();
      const isHLS = videoType === 'application/x-mpegURL' || videoType === 'application/vnd.apple.mpegurl';
      
      // Safari için native HLS kullan
      if (isSafariBrowser && isHLS && playerRef.current.tech_) {
        playerRef.current.tech_.vhs = null; // VHS'yi temizle
      }
      
      playerRef.current.src({
        src: currentLesson.videoUrl,
        type: videoType
      });
      
      playerRef.current.load()
      playerRef.current.currentTime(0);

      // Kaydedilmiş playback rate'i uygula
      playerRef.current.one('loadedmetadata', () => {
        const savedRate = selectedPlaybackRateRef.current;
        if (savedRate && savedRate !== 1) {
          playerRef.current.playbackRate(savedRate);
        }
      });

      // Ended event listener'ı yeniden ekle (güncel currentLesson ile)
      playerRef.current.on('ended', () => {
        if (!isEndedRef.current) {
          isEndedRef.current = true;
          // Video bittiğinde direkt sonraki derse geç
          goToNextLesson();
        }
      });

      // Autoplay (eğer user önceden play'e basmışsa)
      if (hasUserInteractedRef.current) {
        playerRef.current.play().catch(error => {
          if (error.name !== 'NotAllowedError') {
            console.error('Play hatası:', error);
          }
        });
      }

    }
  }, [currentLesson.videoUrl]);

  // Playback rate değiştiğinde uygula (video URL değişmediğinde)
  useEffect(() => {
    if (playerRef.current && playerReady && selectedPlaybackRate) {
      // Sadece playback rate değiştiyse, currentTime'i koruyarak uygula
      const currentRate = playerRef.current.playbackRate();
      if (currentRate !== selectedPlaybackRate) {
        playerRef.current.playbackRate(selectedPlaybackRate);
      }
    }
  }, [selectedPlaybackRate, playerReady]);

  // Seçilen kalite değiştiğinde videoyu güncelle
  useEffect(() => {
    if (playerReady && selectedQuality) {
      changeQuality(selectedQuality);
    }
    
    // Manuel quality selector UI'ını güncelle
    if (playerRef.current) {
      const qualityButton = playerRef.current.el().querySelector('.vjs-safari-quality-selector');
      if (qualityButton && qualityButton.updateSelectedItem) {
        qualityButton.updateSelectedItem(selectedQuality);
      }
    }
  }, [selectedQuality, playerReady, changeQuality]);

  // currentLesson ve completedLessons ref'lerini güncelle
  useEffect(() => {
    currentLessonRef.current = currentLesson;
  }, [currentLesson]);

  useEffect(() => {
    completedLessonsRef.current = completedLessons;
  }, [completedLessons]);

  useEffect(() => {
    selectedPlaybackRateRef.current = selectedPlaybackRate;
  }, [selectedPlaybackRate]);

  // Video son 10 saniyeye girdiğinde tamamlanan ders olarak işaretle ve pozisyonu kaydet
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) return;

    const saveProgress = (position) => {
      const currentLesson = currentLessonRef.current; //closure problemleri olmasın diye tekrar okuyoruz.
      if (!currentLesson) return;
    
      const user = auth.currentUser;
      if (!user) return;
    
      const currentSectionId = getCurrentSectionId(currentLesson.id);
      if (currentSectionId) {
        updateLastWatchedVideo(user.uid, currentLesson.id, currentSectionId, position);
        lastSavedPositionRef.current = position;
      }
    };

    const handleTimeUpdate = () => {
      const duration = player.duration();
      const currentTime = player.currentTime();
      const currentLesson = currentLessonRef.current;
      const completedLessons = completedLessonsRef.current;
      const securityCheck = !duration || !currentLesson

      if (securityCheck) {
        return;
      }

      const son10SaniyeGirdi = duration - currentTime < 10 
      
      if (son10SaniyeGirdi && !completionReportedRef.current) {
        completionReportedRef.current = true;//bu konu videosu için artık tamamlanmıştır.
        const user = auth.currentUser;
        if (user && !completedLessons.includes(currentLesson.id)) {
          addCompletedLesson(user.uid, currentLesson.id);
          setCompletedLessons(prev => (
            prev.includes(currentLesson.id) ? prev : [...prev, currentLesson.id]
          ));
        }
      }

      const sonkayit15SaniyeFark = Math.abs(currentTime - lastSavedPositionRef.current) >= 15
      if (sonkayit15SaniyeFark) {
        const roundedTime = Math.round(currentTime * 10) / 10; // 1 decimal
        saveProgress(roundedTime);
      }

    };

    player.on('timeupdate', handleTimeUpdate);

    return () => {
      player.off('timeupdate', handleTimeUpdate);
    };
  }, [playerReady]);

  // Mevcut lesson'un hangi section'da olduğunu bul
  const getCurrentSectionId = (lessonId) => {
    for (const section of courseData.sections) {
      if (section.lessons.some(lesson => lesson.id === lessonId)) {
        return section.id;
      }
    }
    return null;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  //
  useEffect(() => {
    completionReportedRef.current = completedLessons.includes(currentLesson.id);
  }, [currentLesson.id, completedLessons]);

  const selectLesson = (lesson) => {
    setCurrentLesson(lesson);
    
    // Mevcut lesson'un section'ını bul ve sadece onu açık tut
    const currentSectionId = getCurrentSectionId(lesson.id);
    if (currentSectionId) {
      const newExpandedSections = {};
      newExpandedSections[currentSectionId] = true;
      setExpandedSections(newExpandedSections);
    }

    // Son izlenen videoyu güncelle
    const user = auth.currentUser;
    if (user && currentSectionId) {
      updateLastWatchedVideo(user.uid, lesson.id, currentSectionId);
    }
  };

  const getNextLesson = () => {
    let allLessons = [];
    courseData.sections.forEach(section => {
      allLessons = [...allLessons, ...section.lessons];
    });
    const currentIndex = allLessons.findIndex(l => l.id === currentLesson.id);
    return currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;
  };

  const getPreviousLesson = () => {
    let allLessons = [];
    courseData.sections.forEach(section => {
      allLessons = [...allLessons, ...section.lessons];
    });
    const currentIndex = allLessons.findIndex(l => l.id === currentLesson.id);
    return currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  };

  const goToNextLesson = () => {
    const next = getNextLesson();
    if (next) {
      selectLesson(next);
    }
  };

  const restartVideo = async () => {
    if (playerRef.current) {
      try {
        playerRef.current.currentTime(0);
        await playerRef.current.play();
    } catch (error) {
        console.error('Video restart hatası:', error);
    }
    }
  };

  const nextLesson = getNextLesson();
  const previousLesson = getPreviousLesson();

  // CSS cleanup tamamlanana kadar loading göster
  if (isCleaningUp || !cleanupComplete) {
    return (
      <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen bg-gray-900 text-white items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen bg-gray-900 text-white">
      {/* Sidebar */}
      <div
        className="w-full lg:w-80 2xl:w-1/5 h-auto lg:h-full bg-gray-800 overflow-y-auto flex-shrink-0 lg:order-1 order-2"
        style={computedSidebarStyle}
      >
        <div>
          {/* Ana Sayfaya Dön Butonu */}
          <div className="p-2 sm:p-4 2xl:p-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10 flex items-center justify-center h-[56px] sm:h-[72px] 2xl:h-[88px]">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-base sm:text-lg 2xl:text-2xl font-bold text-white hover:text-purple-400 transition-colors"
              style={{ textDecoration: 'none' }}
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 2xl:w-7 2xl:h-7" />
              <span>Ana Sayfaya Dön</span>
            </Link>
          </div>
          {/* Kurs Başlığı */}
          <div className="p-2 sm:p-4 2xl:p-6 border-b border-gray-700 sticky top-[56px] sm:top-[72px] 2xl:top-[88px] bg-gray-800 z-10 flex items-center justify-center h-[56px] sm:h-[72px] 2xl:h-[88px]">
            <h2 className="text-base sm:text-lg 2xl:text-2xl font-bold">{courseData.title}</h2>
          </div>
          <div className="p-3 sm:p-4 2xl:p-6">
            {courseData.sections.map(section => (
              <div key={section.id} className="mb-4 2xl:mb-6">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-3 2xl:p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <span className="font-semibold text-sm 2xl:text-base">{section.title}</span>
                  {expandedSections[section.id] ? <ChevronUp className="w-5 h-5 2xl:w-6 2xl:h-6" /> : <ChevronDown className="w-5 h-5 2xl:w-6 2xl:h-6" />}
                </button>
                
                {expandedSections[section.id] && (
                  <div className="mt-2 2xl:mt-3 space-y-1 2xl:space-y-2">
                    {section.lessons.map(lesson => (
                      <button
                        key={lesson.id}
                        onClick={() => selectLesson(lesson)}
                        className={`w-full text-left px-3 py-1.5 2xl:px-4 2xl:py-2 rounded-lg transition-colors flex items-start gap-3 2xl:gap-4 ${
                          currentLesson.id === lesson.id 
                            ? 'bg-purple-600' 
                            : 'hover:bg-gray-700'
                        }`}
                      >
                        <div className="mt-1">
                          {completedLessons.includes(lesson.id) ? (
                            <CheckCircle className="w-4 h-4 2xl:w-6 2xl:h-6 text-green-400" />
                          ) : (
                            <PlayCircle className="w-4 h-4 2xl:w-6 2xl:h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm 2xl:text-base font-medium">{lesson.title}</div>
                          <div className="text-xs 2xl:text-sm text-gray-400">{lesson.duration}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className="flex-none lg:flex-1 flex flex-col lg:overflow-auto lg:order-2 order-1">
        {/* Üst Bar */}
        <div className="bg-gray-800 p-2 sm:p-4 2xl:p-6 border-b border-gray-700 sticky top-0 z-20 lg:static h-[56px] sm:h-[72px] 2xl:h-[88px] flex items-center">
          <div className="max-w-[2000px] mx-auto flex items-center justify-between gap-2 sm:gap-4 2xl:gap-6 w-full">
            <div className="flex items-center gap-1 sm:gap-2 2xl:gap-3">
              <button
                onClick={() => previousLesson && selectLesson(previousLesson)}
                disabled={!previousLesson}
                className={`px-3 py-2 sm:px-6 sm:py-3 2xl:px-8 2xl:py-4 rounded-lg transition-colors text-sm sm:text-base 2xl:text-lg ${
                  previousLesson 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-700 opacity-50 cursor-not-allowed'
                }`}
              >
                <span className="hidden sm:inline">← Önceki Video</span>
                <span className="sm:hidden">←</span>
              </button>
            </div>

            <h1 className="text-sm sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-center flex-1 truncate px-2 2xl:px-4">{currentLesson.title}</h1>

            <button
              onClick={() => nextLesson && selectLesson(nextLesson)}
              disabled={!nextLesson}
              className={`px-3 py-2 sm:px-6 sm:py-3 2xl:px-8 2xl:py-4 rounded-lg transition-colors text-sm sm:text-base 2xl:text-lg ${
                nextLesson 
                  ? 'bg-purple-600 hover:bg-purple-700' 
                  : 'bg-gray-700 opacity-50 cursor-not-allowed'
              }`}
            >
              <span className="hidden sm:inline">Sonraki Video →</span>
              <span className="sm:hidden">→</span>
            </button>
          </div>
        </div>

        {/* Video Player - Video.js ile */}
        <div className="flex-none lg:flex-1 bg-black flex items-start justify-center">
          <div className="w-full flex items-start justify-center">
            <div 
              ref={containerRef}
              style={getVideoContainerStyle()}
            >
              <video
                ref={videoRef}
                className="video-js vjs-theme-sea"
                playsInline
                style={{
                  position: isFullscreen ? 'static' : 'absolute',
                  top: isFullscreen ? 'auto' : 0,
                  left: isFullscreen ? 'auto' : 0,
                  width: '100%',
                  height: '100%'
                }}
              />
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LecturePage;